import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../utils/AuthContext';
import { ReleaseAnnouncement } from '../../types';
import {
  subscribeToLatestRelease,
  shouldShowRelease,
  markReleaseSeen,
} from '../../utils/releaseAnnouncements';
import ReleaseAnnouncementPopup from './ReleaseAnnouncementPopup';

/** Delay before showing so the popup doesn't collide with initial page load. */
const SHOW_DELAY_MS = 1500;

/**
 * Mounted once in AuthenticatedRoutes. Subscribes to the newest active
 * release announcement and shows the "What's New" popup when the signed-in
 * user's role is targeted and they haven't dismissed it yet.
 */
const ReleaseAnnouncementGate: React.FC = () => {
  const { user } = useAuth();
  const [release, setRelease] = useState<ReleaseAnnouncement | null>(null);
  const [visible, setVisible] = useState(false);
  // In-session guard so the popup never re-shows before the auth snapshot refreshes.
  const [dismissedIds, setDismissedIds] = useState<string[]>([]);

  useEffect(() => subscribeToLatestRelease(setRelease), []);

  useEffect(() => {
    if (!release || !user) return;
    if (dismissedIds.includes(release.id)) return;
    if (!shouldShowRelease(release, user)) return;
    const timer = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, [release, user, dismissedIds]);

  const handleDismiss = useCallback(() => {
    setVisible(false);
    if (!release) return;
    setDismissedIds(prev => (prev.includes(release.id) ? prev : [...prev, release.id]));
    if (user) {
      markReleaseSeen(user.id, release.id).catch(err => {
        console.warn('Failed to record release dismissal:', err);
      });
    }
  }, [release, user]);

  if (!visible || !release) return null;

  return <ReleaseAnnouncementPopup release={release} onDismiss={handleDismiss} />;
};

export default ReleaseAnnouncementGate;
